import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateQuestions, getResults } from '../../services/generateQuestionService';
import './QuizPage.css';
import RichText from '../../components/common/RichText';

interface Question {
  id: number;
  question: string;
  options: string[];
  timeToSolve: number;
  questionId: string;
}

const Loader: React.FC<{ subject: string }> = ({ subject }) => (
  <div className="loader-container">
    <div className="spinner" />
    <p className="loader-text">Loading questions for <strong>{subject}</strong>...</p>
  </div>
);

const ResultsLoader: React.FC = () => (
  <div className="results-loader-container">
    <div className="results-spinner" />
    <p className="results-loader-text">Processing your results...</p>
  </div>
);

const QuizPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedChapters = [], selectedSubject = '', feedback = '' } = (location.state as {
    selectedChapters?: string[];
    selectedSubject?: string;
    feedback?: string;
  }) || {};

  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionSetId, setQuestionSetId] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, string>>({});
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalTimeLeft, setTotalTimeLeft] = useState<number>(0);
  const [totalTimeLimit, setTotalTimeLimit] = useState<number>(0);
  const [expiryTime, setExpiryTime] = useState<number | null>(null);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const totalTimerRef = useRef<NodeJS.Timeout | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Save quiz state to sessionStorage
  const saveQuizState = useCallback(() => {
    const quizState = {
      questions,
      questionSetId,
      selectedOptions,
      isQuizCompleted,
      totalTimeLeft,
      totalTimeLimit,
      expiryTime,
      selectedChapters,
      selectedSubject,
      feedback
    };
    sessionStorage.setItem('quizState', JSON.stringify(quizState));
  }, [questions, questionSetId, selectedOptions, isQuizCompleted, totalTimeLeft, totalTimeLimit, expiryTime, selectedChapters, selectedSubject, feedback]);

  // Load quiz state from sessionStorage
  const loadQuizState = useCallback(() => {
    const savedState = sessionStorage.getItem('quizState');
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      if (parsedState.selectedSubject === selectedSubject) {
        setQuestions(parsedState.questions);
        setQuestionSetId(parsedState.questionSetId);
        setSelectedOptions(parsedState.selectedOptions || {});
        setIsQuizCompleted(parsedState.isQuizCompleted || false);
        const restoredExpiry = parsedState.expiryTime ?? null;
        const expiryTimestamp = restoredExpiry
          ? Number(restoredExpiry)
          : null;
        setExpiryTime(expiryTimestamp);
        const remainingFromExpiry = expiryTimestamp
          ? Math.max(0, Math.floor((expiryTimestamp - Date.now()) / 1000))
          : parsedState.totalTimeLeft || 0;
        setTotalTimeLeft(remainingFromExpiry);
        setTotalTimeLimit(parsedState.totalTimeLimit || parsedState.totalTimeLeft || 0);
        setTotalQuestions(parsedState.questions?.length || 0);
        return true;
      }
    }
    return false;
  }, [selectedSubject]);

  const handleSubmitQuiz = useCallback(async () => {
    if (!questionSetId) return;

    setIsQuizCompleted(true);
    setIsModalOpen(false);
    if (totalTimerRef.current) clearInterval(totalTimerRef.current);

    try {
      const answers = questions
        .map((q) => ({
          questionId: q.questionId,
          selectedOption: selectedOptions[q.id] || null
        }))
        .filter((a) => a.selectedOption !== null);

      const response = await getResults(questionSetId, answers);
      sessionStorage.removeItem('quizState');
      navigate('/results', { state: { result: response.result } });
    } catch (err) {
      setError('Failed to fetch results. Please try again.');
    }
  }, [navigate, questionSetId, questions, selectedOptions]);

  const handleOptionSelect = (questionId: number, option: string) => {
    const updated = { ...selectedOptions, [questionId]: option };
    setSelectedOptions(updated);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    const handleBackButton = () => {
      sessionStorage.removeItem('quizState');
      navigate("/dashboard", { replace: true });
    };

    window.addEventListener('popstate', handleBackButton);
    const loadQuestions = async () => {
      if (loadQuizState()) {
        setLoading(false);
        return;
      }

      if (!selectedChapters.length || !selectedSubject) {
        setError('No chapters or subject selected.');
        setLoading(false);
        return;
      }

      try {
        const response = await generateQuestions(selectedChapters, selectedSubject, feedback || 'General difficulty');
        const formattedQuestions = response.questions.map((q, index) => ({
          id: index + 1,
          question: q.question,
          options: q.options,
          timeToSolve: q.timeToSolve,
          questionId: q._id,
        }));

        setQuestions(formattedQuestions);
        setQuestionSetId(response.questionSetId);
        setTotalQuestions(formattedQuestions.length);
        const expiryTimestamp = response.expiresAt ? new Date(response.expiresAt).getTime() : null;
        setExpiryTime(expiryTimestamp);
        const totalTimeFromResponse =
          response.totalTimeToSolve ??
          formattedQuestions.reduce((sum, q) => sum + (q.timeToSolve || 0), 0);
        setTotalTimeLimit(totalTimeFromResponse);
        const initialTimeLeft = expiryTimestamp
          ? Math.max(0, Math.floor((expiryTimestamp - Date.now()) / 1000))
          : totalTimeFromResponse;
        setTotalTimeLeft(initialTimeLeft);
      } catch (err) {
        setError('Failed to load questions.');
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();

    return () => {
      if (totalTimerRef.current) clearInterval(totalTimerRef.current);
    };
  }, [selectedChapters, selectedSubject, feedback, loadQuizState]);

  useEffect(() => {
    if (questions.length > 0 && !isQuizCompleted) {
      saveQuizState();
    }
  }, [questions, selectedOptions, totalTimeLeft, isQuizCompleted, saveQuizState]);

  useEffect(() => {
    if (!expiryTime || isQuizCompleted) return;

    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
      setTotalTimeLeft(remaining);
      if (remaining <= 0) {
        handleSubmitQuiz();
      }
    };

    tick();
    totalTimerRef.current = setInterval(tick, 1000);

    return () => {
      if (totalTimerRef.current) {
        clearInterval(totalTimerRef.current);
        totalTimerRef.current = null;
      }
    };
  }, [expiryTime, isQuizCompleted, handleSubmitQuiz]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="quiz-page">
      <div className="quiz-container">
        <div className="quiz-header">
          <h1 className="quiz-title">📝 Mock Test</h1>
          {!isQuizCompleted && (
            <div className="global-timer">
              {totalTimeLimit > 0 && (
                <span className="timer-total">
                  Total Time: {formatTime(totalTimeLimit)}
                </span>
              )}
              <span className="timer-remaining">
                ⏱ Time Left: {formatTime(totalTimeLeft)}
              </span>
            </div>
          )}
        </div>


        {loading ? (
          <Loader subject={selectedSubject} />
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button
              className="retry-button"
              onClick={() => {
                setError(null);
                handleSubmitQuiz();
              }}
            >
              Retry Submission
            </button>
          </div>
        ) : !isQuizCompleted ? (
          <div className="question-section">
            {questions.map((question) => (
              <div key={question.id} className="question-block">
                <div className="question-header">
                  <h2 className="question-title">Question {question.id}</h2>
                  <p className="question-text"><RichText inline text={question.question} /></p>
                </div>
                <div className="options-column">
                  {question.options.map((option, index) => (
                    <button
                      key={index}
                      className={`option-button ${selectedOptions[question.id] === option ? 'selected' : ''}`}
                      onClick={() => handleOptionSelect(question.id, option)}
                    >
                      <RichText inline text={option} />
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button className="quiz-nav-button" onClick={() => setIsModalOpen(true)}>
              Submit Quiz
            </button>
          </div>
        ) : (
          <div className="quiz-results">
            <h2 className="results-title">Quiz Completed!</h2>
            <ResultsLoader />
          </div>
        )}

        {isModalOpen && (
          <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
            <div className="modal-container" ref={modalRef} onClick={(e) => e.stopPropagation()}>
              <h2>Confirm Submission</h2>
              <p>Are you sure you want to submit the quiz?</p>
              <div className="modal-buttons">
                <button ref={cancelButtonRef} className="modal-cancel-button" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button className="modal-submit-button" onClick={handleSubmitQuiz} disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizPage;
